import React, {useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {ActionSheet, Button, Card, List, Toast, Skeleton, Dialog, Popup } from 'antd-mobile';
import {getBottomNavBarContainerStyle} from "utils/deviceUtils";
import {DeliveringPath, DeliveryDetailPath} from "enums/DeliveryNavigatePathEnum";
import EmptyState from "components/EmptyState";
import {useCustomToast} from 'hooks/useCustomToast';
import {useDeliveryTaskOrder, useDeliveryTaskOrderList, useExceptionDictionaries} from "../store/deliveryStore";
import {useLongPress} from 'hooks/useLongPress';
import {timestampToDate} from "utils/dateTimeUtils";
import {formatLocalizedDateTime} from "utils/dateFormatUtils";
import {ExceptionDictionaryType, ExceptionSubReasonType} from '../graphql/queries/getExceptionDictionaryForDelivery';
import {useTranslation} from "react-i18next";
import {t} from "../../../i18n";
import { copyToClipboard } from 'utils/copyToClipboard';
import { useNetwork } from 'hooks/useNetwork';
import { getExceptionTypeText, getLocalizedText } from 'utils/i18nUtils';
import { formatStopNumberFromDeliveryTaskOrder } from 'utils/stopNumberUtils';
import AccessInfoDisplay from '../delivering/components/AccessInfoDisplay';
import OrderNoteItem from 'components/OrderNoteItem/OrderNoteItem';
import {maskPhoneNumber} from 'utils/phoneUtils';
import DeliveryPhoneAndNavigateTemporary, { DeliveryPhoneAndNavigateTemporaryRef } from "./DeliveryPhoneAndNavigateTemporary";
import AddressLocationIncorrectButton from "../delivering/components/AddressLocationIncorrectButton";
import AccessCodeValidationDialog from '../delivering/components/AccessCodeValidationDialog';
import { useAccessCodes } from '../delivering/hooks/useAccessCodes';
import { validateAccessCodeLocation } from '../delivering/utils/accessCodeLocationValidation';
import {getUserInfo} from "utils/auth";
import {closeNavigationModals as closeNavigationModalsUtil, forceCloseNavigationModal} from "utils/navigationUtils";
import { RightOutline, ExclamationCircleOutline, CheckOutline } from 'antd-mobile-icons';
import dayjs from 'dayjs';
import { isSmsTemplateGrayEnabled } from '../../../utils/gray/SmsTemplateModelGray';
import { useBatchConfigEnable } from '../../../graphql/queries/commonConfigQueries';
import { GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK } from '../../../constants/grayConfigKeys';

interface OrderDetailCardProps {
    deliveryTaskOrderId: number;
    trackingNumber: string;
    onClose?: () => void; // 可选的关闭回调
    onBeforeNavigate?: () => void; // 跳转前的回调，用于保存地图状态
    fromMapMode?: boolean; // 是否来自地图模式
    onNavigateToAccessCodeDetail?: (params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => void; // 导航到门禁详情的回调（地图模式使用）
    onNavigateToAddForm?: (params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => void; // 导航到新增表单的回调（地图模式使用）
    clusterInfo?: { // 新增集群信息
        isCluster?: boolean;
        clusterCount?: number;
        clusterAddresses?: any[];
    };
    popupContentView?: 'order' | 'accessCode' | 'addForm' | 'updateForm'; // Popup 内容视图（地图模式使用，用于触发待验证code检查）
    analyzedAddressLine1?: string; // 从父组件传入的分析后的地址（MapModePage使用）
    isNavigationAddressAnalyzeEnabled?: boolean;
}

export interface OrderDetailTemporaryCardRef {
    closeLongPressModal: () => void;
}

const OrderDetailTemporaryCard = forwardRef<OrderDetailTemporaryCardRef, OrderDetailCardProps>(({ 
    deliveryTaskOrderId, 
    trackingNumber,
    onClose,
    onBeforeNavigate,
    fromMapMode,
    onNavigateToAccessCodeDetail,
    onNavigateToAddForm,
    clusterInfo,
    popupContentView,
    analyzedAddressLine1,
    isNavigationAddressAnalyzeEnabled
}, ref) => {
    const navigate = useNavigate();
    const deliveryPhoneAndNavigateRef = useRef<DeliveryPhoneAndNavigateTemporaryRef>(null);
    const { i18n } = useTranslation();

    // 暴露关闭长按弹窗的方法给父组件
    useImperativeHandle(ref, () => ({
        closeLongPressModal: () => {
            // 单个包裹模式：关闭单个导航弹窗
            deliveryPhoneAndNavigateRef.current?.closeLongPressModal();
            // 集群模式：通过 DOM 查询关闭所有打开的导航弹窗（iOS 18 增强）
            closeNavigationModalsUtil();
            // iOS 18 需要更强制的方式关闭弹窗，使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
            requestAnimationFrame(() => {
                forceCloseNavigationModal(
                    t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                    t('pages.delivery.orderDetail.mapCancel') || undefined
                );
            });
        }
    }), []);
    const {showToast} = useCustomToast();
    const {deliveryTaskOrder, loading} = useDeliveryTaskOrder(deliveryTaskOrderId);
    const { deliveryTaskOrderList } = useDeliveryTaskOrderList();
    const exceptionDictionaries = useExceptionDictionaries();

    // 批量检查灰度配置
    const { isEnabled: getConfigEnabled } = useBatchConfigEnable([
        GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK
    ]);

    // 获取各个灰度配置的启用状态
    const isAddressCoordinateFeedbackEnabled = getConfigEnabled(GRAY_CONFIG_ADDRESS_COORDINATE_FEEDBACK);

    const [failedActionSheetVisible, setFailedActionSheetVisible] = useState(false);
    const [selectedExceptionForSubReasons, setSelectedExceptionForSubReasons] = useState<ExceptionDictionaryType | null>(null);
    const [selectedSubReasonCodes, setSelectedSubReasonCodes] = useState<Set<string>>(new Set());
    const [addAccessInfoDialogVisible, setAddAccessInfoDialogVisible] = useState(false);
    const [isValidatingLocation, setIsValidatingLocation] = useState(false);
    const [expandedOrderIds, setExpandedOrderIds] = useState<Record<number, boolean>>({});
    const { isOnline } = useNetwork();
    const [isSmsEnabled, setIsSmsEnabled] = useState(false);
    const [isCheckingSms, setIsCheckingSms] = useState(true);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // 全局统一的当前订单数据（聚合模式和单订单模式通用）
    const currentOrder = useMemo(() => {
        return clusterInfo?.isCluster && clusterInfo?.clusterAddresses?.[0]
            ? clusterInfo.clusterAddresses[0]
            : deliveryTaskOrder;
    }, [clusterInfo?.isCluster, clusterInfo?.clusterAddresses, deliveryTaskOrder]);

    // 派生的常用字段
    const currentTrackingNumber = currentOrder?.trackingNumber || trackingNumber;
    const currentOrderId = currentOrder?.orderWithAddress?.order?.id;
    const currentDspId = currentOrder?.dspId;
    const currentAddressLat = currentOrder?.orderWithAddress?.deliveryAddress?.latitude;
    const currentAddressLon = currentOrder?.orderWithAddress?.deliveryAddress?.longitude;
    const currentAddressId = currentOrder?.orderWithAddress?.deliveryAddress?.id;

    useEffect(() => {
        async function checkSmsGrayStatus() {
            const userInfo = getUserInfo();
            const dspId = currentDspId;
            const driverId = userInfo?.account?.id;

            if (!dspId || !driverId) {
                setIsSmsEnabled(false);
                setIsCheckingSms(false);
                return;
            }

            try {
                const result = await isSmsTemplateGrayEnabled(dspId, parseInt(driverId));
                setIsSmsEnabled(result);
            } catch (error) {
                console.error('Failed to check SMS gray status:', error);
                setIsSmsEnabled(false);
            } finally {
                setIsCheckingSms(false);
            }
        }

        checkSmsGrayStatus();
    }, [currentDspId]);

    const handleRecipientSms = (recipient: any) => {
        const firstOrder = recipient.orders[0];
        const userInfo = getUserInfo();
        if (!firstOrder || !userInfo) return;

        navigate(DeliveringPath.DELIVERY_CHAT, {
            state: {
                stopNumber: firstOrder.stopNumber,
                fullName: recipient.fullName,
                phone: recipient.phone,
                tracking: firstOrder.trackingNumber,
                subStopNumber: firstOrder.subStopNumber,
                driverId: parseInt(userInfo.account.id),
                orderAddressId: firstOrder.orderWithAddress?.deliveryAddress?.id || firstOrder.addressId,
                driverName: userInfo.account.name
            }
        });
    };

    // 根据是否聚合选择显示地址
    const displayAddress = useMemo(() => {
        // 聚合模式：优先使用 clusterAddresses 中的第一个订单（因为地址都一样）
        // 单条模式：使用 deliveryTaskOrder
        const sourceOrder = currentOrder;

        if (!sourceOrder) return '';

        if (clusterInfo?.isCluster) {
            // 聚合订单：优先显示标准地址
            return sourceOrder.orderWithAddress?.deliveryAddress?.standardizedAddressString || [
                sourceOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
                sourceOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
                sourceOrder.orderWithAddress?.deliveryAddress?.city || '',
                sourceOrder.orderWithAddress?.deliveryAddress?.state || '',
                sourceOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
                'US'
            ].filter(Boolean).join(' ');
        }

        // 单条订单：只显示拼接地址
        return [
            sourceOrder.orderWithAddress?.deliveryAddress?.addressLine1 || '',
            sourceOrder.orderWithAddress?.deliveryAddress?.addressLine2 || '',
            sourceOrder.orderWithAddress?.deliveryAddress?.city || '',
            sourceOrder.orderWithAddress?.deliveryAddress?.state || '',
            sourceOrder.orderWithAddress?.deliveryAddress?.zipcode || '',
            'US'
        ].filter(Boolean).join(' ');
    }, [currentOrder, clusterInfo?.isCluster]);
    // 长按复制地址
    const { handleLongPressStart, handleLongPressEnd } = useLongPress(async () => {
        if (!displayAddress || displayAddress.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(displayAddress);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.addressCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    }, 500);

    const toggleOrderExpansion = (orderId: number) => {
        setExpandedOrderIds(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    // 处理多级聚合数据
    const groupedData = useMemo(() => {
        if (!clusterInfo?.isCluster || !clusterInfo?.clusterAddresses) {
            // 如果不是集群，也包装成一样的结构方便渲染
            if (!deliveryTaskOrder) return [];
            const orders = [deliveryTaskOrder];
            return processOrders(orders);
        }
        // 聚合模式：用 store 中的最新数据替换旧数据
        const latestClusterAddresses = clusterInfo.clusterAddresses.map(order => {
            // 从 store 中找到最新的订单数据
            const latestOrder = deliveryTaskOrderList.find(
                item => item.id === order.deliveryTaskOrderId || item.id === order.id
            );
            return latestOrder || order; // 如果找到最新数据就用最新的，否则用原数据
        }).filter(order => order);
        return processOrders(latestClusterAddresses);
    }, [clusterInfo, deliveryTaskOrder, deliveryTaskOrderList]);
    function processOrders(orders: any[]) {
        // 第一步：按日期聚合 (YYYY-MM-DD)
        const dateGroups = new Map<string, any[]>();
        orders.forEach(order => {
            const date = dayjs(timestampToDate(order.taskDate)).format('YYYY-MM-DD');
            if (!dateGroups.has(date)) {
                dateGroups.set(date, []);
            }
            dateGroups.get(date)!.push(order);
        });

        // 日期升序排序
        const sortedDates = Array.from(dateGroups.keys()).sort((a, b) => dayjs(a).isBefore(dayjs(b)) ? -1 : 1);

        return sortedDates.map(date => {
            const dateOrders = dateGroups.get(date)!;
            // 第二步：按收件人姓名+手机号聚合
            const recipientGroups = new Map<string, any[]>();
            dateOrders.forEach(order => {
                const key = `${order.fullName || order.orderWithAddress?.deliveryAddress?.fullName}_${order.phoneNumber || order.orderWithAddress?.deliveryAddress?.phoneNumber}`;
                if (!recipientGroups.has(key)) {
                    recipientGroups.set(key, []);
                }
                recipientGroups.get(key)!.push(order);
            });

            // 收件人组内部排序 (按 stopNumber)
            const recipients = Array.from(recipientGroups.entries()).map(([key, orders]) => {
                const sortedOrders = [...orders].sort((a, b) => (a.stopNumber || 0) - (b.stopNumber || 0));
                return {
                    key,
                    fullName: sortedOrders[0].fullName || sortedOrders[0].orderWithAddress?.deliveryAddress?.fullName,
                    phone: sortedOrders[0].phoneNumber || sortedOrders[0].orderWithAddress?.deliveryAddress?.phoneNumber,
                    extension: sortedOrders[0].extension || sortedOrders[0].orderWithAddress?.deliveryAddress?.extension,
                    orders: sortedOrders
                };
            });

            // 日期组内的收件人排序 (按最小 stopNumber)
            recipients.sort((a, b) => (a.orders[0].stopNumber || 0) - (b.orders[0].stopNumber || 0));

            // 计算日期标签 (今日/昨日等)
            let dateLabel = '';
            const today = dayjs().format('YYYY-MM-DD');
            const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            if (date === today) dateLabel = `(${t('pages.delivery.delivering.today')})`;
            else if (date === yesterday) dateLabel = `(${t('pages.delivery.delivering.yesterday')})`;

            return {
                date,
                dateLabel,
                recipients
            };
        });
    }

    // 处理新增Code按钮点击（先校验地址）
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const handleAddCodeClick = async () => {
        const addressLat = currentAddressLat as number | undefined;
        const addressLon = currentAddressLon as number | undefined;

        // 延迟显示loading（200ms），如果响应很快则不显示，避免闪烁
        loadingTimeoutRef.current = setTimeout(() => {
            setIsValidatingLocation(true);
        }, 200);

        try {
            await validateAccessCodeLocation({
                addressLat,
                addressLon,
                onValidationPassed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                    setAddAccessInfoDialogVisible(true);
                },
                onValidationFailed: () => {
                    // 清除延迟显示loading的定时器
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }
                    setIsValidatingLocation(false);
                },
                isFormSubmit: false
            });
        } catch (error) {
            // 清除延迟显示loading的定时器
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
            setIsValidatingLocation(false);
            console.error('位置校验失败:', error);
        }
    };

    // 门禁码管理Hook
    const {
        accessCodes,
        pendingAccessCodes,
        validationDialogVisible,
        setValidationDialogVisible,
        handleValidationComplete,
        checkPendingAccessCodes,
        isLoadingAccessCodes,
        isFeatureEnabled,
        isCheckingFeature
    } = useAccessCodes({
        orderId: currentOrderId,
        loading,
        addressLat: currentAddressLat as number | undefined,
        addressLon: currentAddressLon as number | undefined
    });

    // 当 deliveryTaskOrderId 变化时，重置弹框状态（防止切换stop时弹框残留）
    useEffect(() => {
        setAddAccessInfoDialogVisible(false);
        setValidationDialogVisible(false);
    }, [deliveryTaskOrderId, setValidationDialogVisible]);

    // 处理恢复滚动位置
    useEffect(() => {
        const isReturning =
            sessionStorage.getItem('returningFromDetail') === 'true' ||
            sessionStorage.getItem('pickUpSuccess') === 'true';
        if (isReturning && !loading) {
            const savedScrollTop = sessionStorage.getItem('orderDetailCardScrollTop');
            if (savedScrollTop && scrollContainerRef.current) {
                const top = parseInt(savedScrollTop, 10);
                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop = top;
                    }
                }, 150);
            }
            // 注意：这里不要清除 returningFromDetail，由父组件 MapModeTemporaryPage 清除
            sessionStorage.removeItem('orderDetailCardScrollTop');
        }
    }, [loading, deliveryTaskOrderId]);

    const handleDeliverySuccess = async () => {
        // 跳转前保存地图状态
        onBeforeNavigate?.();

        // 基于当前订单构建 parcels 数据
        if (!deliveryTaskOrder) return;
        
        const parcels = [{
            trackingNumber: deliveryTaskOrder.trackingNumber || '',
            stopNumber: formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder),
            deliveryTaskOrderId: Number(deliveryTaskOrder.id)
        }];

        //无网络情况
        if (!isOnline) {
            const deliveryTaskOrderIdList = deliveryTaskOrder?.id
                ? [Number(deliveryTaskOrder.id)]
                : [];

            // 直接导航到下一页
            navigate("/delivery/temporary/delivery", {
                state: {
                    scanResults: parcels,
                    deliveryTaskOrderIdList: deliveryTaskOrderIdList,
                    fromMapMode: fromMapMode
                },
                replace: true
            });
            onClose?.(); // 关闭弹窗
            return;
        }

        // 有网络情况：跳转到扫描验证页面
        navigate('/delivery/scan-verification-temporary', {
            state: {
                targetPath: "/delivery/temporary/delivery",
                parcels: parcels,
                fromMapMode: fromMapMode
            }
        });
        onClose?.(); // 关闭弹窗
    };

    const handleBatchDeliverySuccess = async () => {
        // 保存滚动位置
        if (scrollContainerRef.current) {
            sessionStorage.setItem('orderDetailCardScrollTop', scrollContainerRef.current.scrollTop.toString());
        }

        // 跳转前保存地图状态
        onBeforeNavigate?.();

        // 地图模式：跳转流程会触发关闭弹窗，但需要保留 marker 以便返回时自动恢复
        if (fromMapMode) {
            sessionStorage.setItem('mapMode_preserve_selected_marker', 'true');
        }
        const allOrders = clusterInfo?.clusterAddresses || [];
        const parcels = allOrders.map((order: any) => ({
            trackingNumber: order.trackingNumber,
            stopNumber: formatStopNumberFromDeliveryTaskOrder(order),
            deliveryTaskOrderId: Number(order.id)
        }));
        navigate('/delivery/scan-verification-temporary', {
            state: {
                targetPath: "/delivery/temporary/batch-delivery",
                parcels: parcels,
                fromMapMode: fromMapMode
            }
        });

        onClose?.(); // 关闭弹窗
    };

    const navigateOrderDetail = (currentTrackingNumber: string) => {
        navigate(DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL.replace(':trackingNumber', currentTrackingNumber),{
            state:{
                deliveryTaskOrderId: deliveryTaskOrderId,
                trackingNumber:currentTrackingNumber,
                detailType: 'delivered',
                fromMapMode: fromMapMode
            }
        });
    }

    const handleFailedActionSheetVisible = async () => {
        if (exceptionDictionaries.length === 0){
            showToast('fail', 'No exception actions available.', 'center');
            return;
        }
        setFailedActionSheetVisible(true);
    };

      // 处理来自原生的派送成功回调
    const handleDeliverySuccessFromNative = () => {
        handleDeliverySuccess();
    };

    // 处理来自原生的派送失败回调
    const handleDeliveryFailedFromNative = () => {
        handleFailedActionSheetVisible();
    };
    
    const handleActionClick = async (action: ExceptionDictionaryType) => {
        setFailedActionSheetVisible(false);
        
        // 保存滚动位置
        if (scrollContainerRef.current) {
            sessionStorage.setItem('orderDetailCardScrollTop', scrollContainerRef.current.scrollTop.toString());
        }

        // 跳转前保存地图状态
        onBeforeNavigate?.();
        if (clusterInfo?.isCluster) {
            // 地图模式：跳转流程会触发关闭弹窗，但需要保留 marker 以便返回时自动恢复
            if (fromMapMode) {
                sessionStorage.setItem('mapMode_preserve_selected_marker', 'true');
            }

            // 集群模式：跳转至批量校验页
            const allOrders = clusterInfo?.clusterAddresses || [];
            const parcels = allOrders.map((order: any) => ({
                trackingNumber: order.trackingNumber,
                stopNumber: formatStopNumberFromDeliveryTaskOrder(order),
                deliveryTaskOrderId: Number(order.id)
            }));

            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/batch-failed",
                    parcels: parcels,
                    exceptionReason: action,
                    fromMapMode: fromMapMode
                }
            });
        } else {
            // 单个订单模式：跳转至单个异常标记页
            const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
            if (!isOnline){
                navigate("/delivery/temporary/mark-exception",{
                    state:{
                        deliveryTaskOrderId: deliveryTaskOrderId,
                        trackingNumber:currentTrackingNumber,
                        exceptionReason: action,
                        fromMapMode: fromMapMode
                    }
                });
                onClose?.(); // 关闭弹窗
                return;
            }

            // 单个订单模式：先跳转扫码校验页，再到异常标记页
            const parcels = [
                {
                    trackingNumber: currentTrackingNumber,
                    stopNumber: deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0',
                    deliveryTaskOrderId: deliveryTaskOrderId,
                },
            ];
            navigate('/delivery/scan-verification-temporary', {
                state:{
                    targetPath: "/delivery/temporary/mark-exception",
                    parcels: parcels,
                    exceptionReason: action,
                    fromMapMode: fromMapMode
                }
            });
        }
        
        onClose?.(); // 关闭弹窗
    };

    const handleSubReasonConfirm = (action: ExceptionDictionaryType, subReasons: ExceptionSubReasonType[]) => {
        setSelectedExceptionForSubReasons(null);
        setSelectedSubReasonCodes(new Set());
        if (scrollContainerRef.current) {
            sessionStorage.setItem('orderDetailCardScrollTop', scrollContainerRef.current.scrollTop.toString());
        }
        onBeforeNavigate?.();

        if (clusterInfo?.isCluster) {
            if (fromMapMode) {
                sessionStorage.setItem('mapMode_preserve_selected_marker', 'true');
            }
            const allOrders = clusterInfo?.clusterAddresses || [];
            const parcels = allOrders.map((order: any) => ({
                trackingNumber: order.trackingNumber,
                stopNumber: formatStopNumberFromDeliveryTaskOrder(order),
                deliveryTaskOrderId: Number(order.id)
            }));
            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/batch-failed",
                    parcels: parcels,
                    exceptionReason: action,
                    exceptionSubReasons: subReasons,
                    fromMapMode: fromMapMode
                }
            });
        } else {
            const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
            if (!isOnline) {
                navigate("/delivery/temporary/mark-exception", {
                    state: {
                        deliveryTaskOrderId: deliveryTaskOrderId,
                        trackingNumber: currentTrackingNumber,
                        exceptionReason: action,
                        exceptionSubReasons: subReasons,
                        fromMapMode: fromMapMode
                    }
                });
                onClose?.();
                return;
            }
            const parcels = [
                {
                    trackingNumber: currentTrackingNumber,
                    stopNumber: deliveryTaskOrder ? formatStopNumberFromDeliveryTaskOrder(deliveryTaskOrder) : '0',
                    deliveryTaskOrderId: deliveryTaskOrderId,
                },
            ];
            navigate('/delivery/scan-verification-temporary', {
                state: {
                    targetPath: "/delivery/temporary/mark-exception",
                    parcels: parcels,
                    exceptionReason: action,
                    exceptionSubReasons: subReasons,
                    fromMapMode: fromMapMode
                }
            });
        }
        onClose?.();
    };

    // 地址定位不对点击事件
    const handleAddressIncorrect = () => {
        onBeforeNavigate?.();
        const targetPath = DeliveringPath.DELIVERY_ADDRESS_COORDINATE_FEEDBACK.replace(':trackingNumber', currentTrackingNumber);
        navigate(targetPath, {
            state: {
                deliveryTaskOrderId: currentOrder?.id || deliveryTaskOrderId,
                trackingNumber: currentTrackingNumber,
                originalLatitude: currentAddressLat,
                originalLongitude: currentAddressLon,
                address: displayAddress,
                stopNumber: currentOrder?.stopNumber ? formatStopNumberFromDeliveryTaskOrder(currentOrder) : '0'
            }
        });
        onClose?.();
    };

    const handleCopyAddress = async () => {
        if (!displayAddress || displayAddress.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(displayAddress);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.addressCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    };

    // 复制 tracking number 的函数
    const handleCopyTrackingNumber = async (trackingNumber: string) => {
        if (!trackingNumber || trackingNumber.trim() === '') {
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(trackingNumber);
            if (success) {
                Toast.show({
                    content: t('pages.delivery.delivering.trackingNumberCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.delivery.delivering.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy tracking number failed:', error);
            Toast.show({
                content: t('pages.delivery.delivering.copyFailed'),
                position: 'bottom',
            });
        }
    };

    if (loading) {
        return (
            <div className="p-4">
                <Skeleton style={{width: '100%',height: '45%'}} animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
                <Skeleton.Title style={{width: '100%'}} animated />
            </div>
        );
    }

    if (!deliveryTaskOrder && (!clusterInfo?.clusterAddresses || clusterInfo.clusterAddresses.length === 0)) {
        return (
            <div className="p-4">
                <EmptyState/>
            </div>
        );
    }

    const phone = currentOrder?.orderWithAddress?.deliveryAddress?.phoneNumber;
    const phoneNumberParts = phone?.split(',') || [];
    const mainPhone = phoneNumberParts[0] || phone;
    const phoneExtension = phoneNumberParts[1] ? phoneNumberParts[1].replace('#', '') : currentOrder?.orderWithAddress?.deliveryAddress?.extension;
    const fullName = currentOrder?.orderWithAddress?.deliveryAddress?.fullName;
    const stopNumber = currentOrder ? formatStopNumberFromDeliveryTaskOrder(currentOrder) : '0';
    const routeName = currentOrder?.routeName;
    const deliverAt = currentOrder?.orderWithAddress?.order?.deliverAt;
    const deliveryAttempts = currentOrder?.orderWithAddress?.order?.deliveryAttempts || 0;
    // 单条订单的样式（维持原样）
    if (!clusterInfo?.isCluster) {
        return (
            <div className="flex flex-col h-full relative bg-gray-50">
                {/* 位置校验Loading遮罩 */}
                {isValidatingLocation && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 shadow-lg">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"/>
                                <div className="text-gray-700 text-base">{t('pages.delivery.orderDetail.loading')}</div>
                            </div>
                        </div>
                    </div>
                )}
                {/* 可滚动的内容区域 */}
                <div className="flex-1 overflow-y-auto">
                    {/* Order Details Card */}
                    <Card className="m-2 rounded-lg flex flex-col">
                        {/* 置顶的 trackingNumber */}
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 overflow-hidden">
                            <h3 className="text-sm font-medium text-gray-700 whitespace-nowrap leading-relaxed">{deliveryTaskOrder?.trackingNumber || trackingNumber}</h3>
                        </div>

                        {/* 主要内容区域 */}
                        <div className="flex items-start px-4 py-4 gap-3 sm:gap-4">
                            {/* 左边居中的 stopNumber - 与 trackingNumber 左对齐 */}
                            <div className="flex flex-col items-center justify-center flex-shrink-0 w-10 h-24">
                                <p className="text-2xl font-bold whitespace-nowrap mb-2">{stopNumber}</p>
                                <p className="text-sm text-gray-500 text-center leading-tight">{t('pages.stop')}</p>
                            </div>
                            {/* 详细信息 - 尽量展开，优化为两组信息 */}
                            <div className="flex flex-col gap-3 flex-1 min-w-0 relative">
                                {isAddressCoordinateFeedbackEnabled && (
                                    <AddressLocationIncorrectButton
                                        deliveryTaskOrderId={deliveryTaskOrderId}
                                        trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                        address={displayAddress}
                                        latitude={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude}
                                        longitude={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude}
                                        stopNumber={stopNumber}
                                        className="absolute -top-4 right-0 text-sm text-red-600 px-0 py-0 z-10"
                                    />
                                )}
                                {/* 第一组：主-收件人名称；副-手机号 */}
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-medium break-words">{fullName}</h3>
                                    <div className="text-sm text-gray-500 break-all">{`${maskPhoneNumber(mainPhone || '')}${phoneExtension ? `-${phoneExtension}` : ''}`}</div>
                                </div>
                                {/* 第二组：主-运单地址；副-门禁信息 */}
                                <div className="flex flex-col gap-1">
                                    <div className="text-blue-500 text-base">
                                        <div className="flex items-start gap-2">
                                            <span className="break-words flex-1 min-w-0 leading-relaxed">{displayAddress}</span>
                                            <button
                                                onClick={handleCopyAddress}
                                                className="p-1 flex items-center justify-center flex-shrink-0"
                                                style={{ minWidth: '24px', height: '24px' }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M7 4V2C7 1.45 7.45 1 8 1H20C20.55 1 21 1.45 21 2V16C21 16.55 20.55 17 20 17H18V19C18 20.1 17.1 21 16 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7V4M7 7H4V19H16V7H7Z"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    {/* 门禁信息展示 */}
                                    <AccessInfoDisplay
                                        accessCodes={accessCodes}
                                        addressId={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id}
                                        trackingNumber={deliveryTaskOrder?.trackingNumber || trackingNumber}
                                        address={displayAddress}
                                        addressLat={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined}
                                        addressLon={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined}
                                        orderId={deliveryTaskOrder?.orderWithAddress?.order?.id}
                                        onAddCode={handleAddCodeClick}
                                        showAddButton={true}
                                        isLoading={isLoadingAccessCodes || isCheckingFeature}
                                        fromMapMode={fromMapMode}
                                        deliveryTaskOrderId={deliveryTaskOrderId}
                                        onNavigateToAccessCodeDetail={onNavigateToAccessCodeDetail}
                                        isFeatureEnabled={isFeatureEnabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <DeliveryPhoneAndNavigateTemporary
                            ref={deliveryPhoneAndNavigateRef}
                            tracking={deliveryTaskOrder?.trackingNumber || trackingNumber}
                            phone={mainPhone || ''}
                            address={displayAddress}
                            addressLine1={analyzedAddressLine1}
                            isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                            longitude={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude}
                            latitude={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude}
                            stopNumber={deliveryTaskOrder?.stopNumber.toString() || '0'}
                            fullName={fullName}
                            subStopNumber={deliveryTaskOrder?.subStopNumber}
                            driverId={parseInt(getUserInfo()!!.account.id)}
                            driverName={getUserInfo()!!.account.name}
                            onDeliverySuccessFromNative={handleDeliverySuccessFromNative}
                            onDeliveryFailedFromNative={handleDeliveryFailedFromNative}
                            orderAddressId={deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id!}
                            dspId={deliveryTaskOrder?.dspId!}
                            callText={t('pages.delivery.orderDetail.newCall')}
                        />

                        <List
                            style={{
                                '--border-top': 'none',
                                '--border-bottom': 'none',
                                '--padding-left' : '0px',
                                '--padding-right' : '0px',
                            }}
                        >
                        <List.Item title={`${t('pages.delivery.orderDetail.route')}:`} extra={routeName || '-'} />
                        <List.Item title={`${t('pages.delivery.orderDetail.assignedTime')}:`} extra={deliverAt ? formatLocalizedDateTime(timestampToDate(deliverAt)) : ''}/>
                        <List.Item title={`${t('pages.delivery.orderDetail.deliveryAttempt')}:`} extra={`${deliveryAttempts} out of 3`}/>
                            <List.Item
                                className="[&_.adm-list-item-content-main]:p-0"
                                title=""
                                extra={
                                    <OrderNoteItem 
                                        orderNote={deliveryTaskOrder?.orderNote}
                                        currentLanguage={i18n.language}
                                        className="w-full"
                                    />
                                }
                            />
                        </List>
                    </Card>
                </div>

                {/* 固定在底部的按钮区域 */}
                <div {...getBottomNavBarContainerStyle('flex px-4 py-2 pb-4 gap-4 flex-shrink-0 bg-white border-t border-gray-100')}>
                    <Button
                        color='default'
                        onClick={handleFailedActionSheetVisible}
                        block
                        className="h-12 text-lg font-bold"
                        style={{
                            '--background-color': '#6B7280',
                            '--text-color': 'white',
                            border: 'none'
                        }}
                    >
                        {t('pages.delivery.orderDetail.failed')}
                    </Button>
                    <Button
                        color='primary'
                        onClick={handleDeliverySuccess}
                        block
                        className="h-12 text-lg font-bold"
                        style={{
                            '--background-color': '#4F46E5',
                            border: 'none'
                        }}
                    >
                        {t('pages.delivery.orderDetail.delivered')}
                    </Button>
                </div>

                <ActionSheet
                    visible={failedActionSheetVisible}
                    extra={
                        <div className="text-gray-500">
                            <h3 className={`text-center`}>{t('pages.delivery.orderDetail.failed')}</h3>
                            <span>{t('pages.delivery.orderDetail.failedDesc')}</span>
                        </div>
                    }
                    cancelText={t('pages.delivery.detail.cancel')}
                    onClose={() => setFailedActionSheetVisible(false)}
                    actions={exceptionDictionaries.map((action: ExceptionDictionaryType) => ({
                        text: (
                            <div>
                                <div className="text-purple-700 font-medium text-lg">
                                    {getExceptionTypeText(action)}
                                </div>
                            </div>
                        ),
                        key: action.exceptionReasonCode,
                        onClick: () => {
                            setFailedActionSheetVisible(false);
                            const subReasons = action.exceptionSubReasons;
                            if (subReasons && subReasons.length > 0) {
                                setSelectedExceptionForSubReasons(action);
                                setSelectedSubReasonCodes(new Set());
                            } else {
                                handleActionClick(action);
                            }
                        }
                    }))}
                />

                <Popup
                    visible={selectedExceptionForSubReasons !== null}
                    position="bottom"
                    onMaskClick={() => {
                        setSelectedExceptionForSubReasons(null);
                        setSelectedSubReasonCodes(new Set());
                    }}
                    bodyStyle={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
                >
                    <div className="p-4 pb-safe">
                        <h3 className="text-center text-gray-800 font-semibold text-base py-3">
                            {selectedExceptionForSubReasons ? t('pages.delivery.orderDetail.wrongAddressReason') : ''}
                        </h3>
                        <div className="max-h-60 overflow-y-auto mb-4 space-y-2 scrollbar-always-visible pr-3.5">
                            {(selectedExceptionForSubReasons?.exceptionSubReasons ?? []).map(sub => {
                                const checked = selectedSubReasonCodes.has(sub.code);
                                return (
                                    <div
                                        key={sub.code}
                                        className={`flex items-center justify-between py-3 px-4 rounded-xl border-2 bg-white active:opacity-90 ${
                                            checked ? 'border-purple-600' : 'border-gray-200'
                                        }`}
                                        onClick={() => {
                                            setSelectedSubReasonCodes(prev => {
                                                const next = new Set(prev);
                                                if (checked) next.delete(sub.code);
                                                else next.add(sub.code);
                                                return next;
                                            });
                                        }}
                                    >
                                        <span className={`text-base ${checked ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                                            {getLocalizedText({ zh: sub.textZh, en: sub.textEn, es: sub.textEs }, sub.textEn)}
                                        </span>
                                        <div
                                            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                checked ? 'bg-purple-600' : 'border-2 border-gray-300 bg-white'
                                            }`}
                                        >
                                            {checked ? (
                                                <CheckOutline className="text-white" style={{ fontSize: 16 }} />
                                            ) : (
                                                <CheckOutline className="text-gray-300" style={{ fontSize: 16 }} />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Button
                            block
                            color="primary"
                            className="w-full"
                            style={{ '--background-color': '#7c3aed', '--border-radius': '8px' } as React.CSSProperties}
                            disabled={selectedSubReasonCodes.size === 0}
                            onClick={() => {
                                if (selectedExceptionForSubReasons) {
                                    const subReasons = (selectedExceptionForSubReasons.exceptionSubReasons ?? []).filter(
                                        sub => selectedSubReasonCodes.has(sub.code)
                                    );
                                    handleSubReasonConfirm(selectedExceptionForSubReasons, subReasons);
                                }
                            }}
                        >
                            {t('pages.delivery.orderDetail.confirm')}
                        </Button>
                    </div>
                </Popup>

                {/* 新增门禁信息确认弹窗 */}
                <Dialog
                    visible={addAccessInfoDialogVisible}
                    content={
                        <div className="py-2">
                            <div className="text-base font-semibold mb-3 text-center">
                                {t('pages.delivery.orderDetail.addAccessInfoTitle')}
                            </div>
                            <div className="text-base mb-4 text-center">
                                {t('pages.delivery.orderDetail.addAccessInfoConfirm')}
                            </div>
                        </div>
                    }
                    actions={[
                        [
                            {
                                key: 'cancel',
                                text: t('pages.delivery.orderDetail.cancel'),
                                onClick: () => setAddAccessInfoDialogVisible(false),
                                style: { color: '#4b5563' }
                            },
                            {
                                key: 'confirm',
                                text: t('pages.delivery.orderDetail.confirm'),
                                style: { color: '#60a5fa' },
                                onClick: () => {
                                    setAddAccessInfoDialogVisible(false);
                                    const currentTrackingNumber = deliveryTaskOrder?.trackingNumber || trackingNumber;
                                    const orderId = deliveryTaskOrder?.orderWithAddress?.order?.id;

                                    // 如果在地图模式下且有回调，使用回调在抽屉内显示表单
                                    if (fromMapMode && onNavigateToAddForm && orderId) {
                                        onNavigateToAddForm({
                                            orderId: orderId,
                                            addressId: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id,
                                            trackingNumber: currentTrackingNumber,
                                            address: displayAddress,
                                            addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
                                            addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined
                                        });
                                    } else {
                                        // 否则使用路由跳转（非地图模式）
                                        navigate(
                                            DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ADD_ACCESS_CODE.replace(
                                                ':trackingNumber',
                                                currentTrackingNumber
                                            ),
                                            {
                                                state: {
                                                    addressId: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.id,
                                                    trackingNumber: currentTrackingNumber,
                                                    address: displayAddress,
                                                    addressLat: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.latitude as number | undefined,
                                                    addressLon: deliveryTaskOrder?.orderWithAddress?.deliveryAddress?.longitude as number | undefined,
                                                    orderId: orderId // 传递订单ID
                                                }
                                            }
                                        );
                                    }
                                }
                            }
                        ]
                    ]}
                    onClose={() => setAddAccessInfoDialogVisible(false)}
                />

                {/* 有效性确认弹窗 */}
                <AccessCodeValidationDialog
                    visible={validationDialogVisible}
                    pendingAccessCodes={pendingAccessCodes}
                    onValidationComplete={handleValidationComplete}
                    onClose={() => setValidationDialogVisible(false)}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full relative bg-gray-50">
            {/* 位置校验Loading遮罩 */}
            {isValidatingLocation && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 shadow-lg">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto mb-3"/>
                            <div className="text-gray-700 text-base">{t('pages.delivery.orderDetail.loading')}</div>
                        </div>
                    </div>
                </div>
            )}
            
            {isAddressCoordinateFeedbackEnabled && (
                <div className="px-4 pt-3 flex-none flex justify-end">
                    <span
                        className="text-red-600 text-sm cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1"
                        onClick={handleAddressIncorrect}
                    >
                        <ExclamationCircleOutline fontSize={14} />
                        {t('pages.delivery.addressCoordinateFeedback.addressLocationIncorrect')}
                    </span>
                </div>
            )}
            
            {/* 顶部固定地址区域 */}
            <div className="px-4 pt-2 flex-none">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                            <h2 
                                className="text-lg font-bold text-gray-800 leading-tight break-words cursor-pointer active:opacity-70 transition-opacity"
                                onTouchStart={handleLongPressStart}
                                onTouchEnd={handleLongPressEnd}
                                onMouseDown={handleLongPressStart}
                                onMouseUp={handleLongPressEnd}
                                onMouseLeave={handleLongPressEnd}
                            >
                                {displayAddress}
                            </h2>
                        </div>
                        <div className="flex-none">
                            <DeliveryPhoneAndNavigateTemporary
                                ref={deliveryPhoneAndNavigateRef}
                                tracking={currentTrackingNumber}
                                phone={phone || ''}
                                address={displayAddress}
                                addressLine1={analyzedAddressLine1}
                                isNavigationAddressAnalyzeEnabled={isNavigationAddressAnalyzeEnabled}
                                longitude={currentAddressLon}
                                latitude={currentAddressLat}
                                stopNumber={stopNumber}
                                fullName={fullName}
                                subStopNumber={currentOrder?.subStopNumber}
                                driverId={parseInt(getUserInfo()!!.account.id)}
                                driverName={getUserInfo()!!.account.name}
                                onDeliverySuccessFromNative={handleDeliverySuccessFromNative}
                                onDeliveryFailedFromNative={handleDeliveryFailedFromNative}
                                orderAddressId={currentAddressId!}
                                dspId={currentDspId!}
                                onlyShowNavigate={true}
                            />
                        </div>
                    </div>
                    <div className="mt-2">
                        <AccessInfoDisplay
                            accessCodes={accessCodes}
                            addressId={currentAddressId}
                            trackingNumber={currentTrackingNumber}
                            address={displayAddress}
                            addressLat={currentAddressLat as number | undefined}
                            addressLon={currentAddressLon as number | undefined}
                            orderId={currentOrderId}
                            onAddCode={handleAddCodeClick}
                            showAddButton={true}
                            isLoading={isLoadingAccessCodes || isCheckingFeature}
                            fromMapMode={fromMapMode}
                            deliveryTaskOrderId={deliveryTaskOrderId}
                            onNavigateToAccessCodeDetail={onNavigateToAccessCodeDetail}
                            isFeatureEnabled={isFeatureEnabled}
                        />
                    </div>
                </div>
            </div>

            {/* 可滚动的内容区域 */}
            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-2">
                {groupedData.map((dateGroup, dateIdx) => (
                    <div key={dateGroup.date} className="mt-2 mb-4">
                        {dateGroup.recipients.map((recipient, recIdx) => (
                            <div key={recipient.key} className="bg-white rounded-xl shadow-sm mb-4 border border-gray-100 overflow-hidden">
                                {/* 日期标题移入卡片 */}
                                <div className="px-4 py-3 bg-gray-50/50 border-b border-gray-50">
                                    <div className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <span>{dayjs(dateGroup.date).format('MM-DD')}</span>
                                        <span className="text-gray-500 font-normal">{dateGroup.dateLabel}</span>
                                    </div>
                                </div>

                                {/* 包裹列表 */}
                                <div className="p-4 space-y-4">
                                    {recipient.orders.map((order, orderIdx) => {
                                        // TrackingNumber 中间省略显示逻辑
                                        const trackingNumber = order.trackingNumber || '';
                                        const getMiddleEllipsisText = (text: string, maxLength: number = 20) => {
                                            if (text.length <= maxLength) return text;
                                            const ellipsis = '...';
                                            const charsToShow = maxLength - ellipsis.length;
                                            const frontChars = Math.ceil(charsToShow / 2);
                                            const backChars = Math.floor(charsToShow / 2);
                                            return text.substring(0, frontChars) + ellipsis + text.substring(text.length - backChars);
                                        };
                                        const displayTrackingNumber = getMiddleEllipsisText(trackingNumber, 20);

                                        return (
                                            <div key={order.id} className="flex items-start gap-3 group">
                                                <div className="text-2xl font-black text-gray-800 w-16 flex-shrink-0 text-center flex items-center justify-center h-12 whitespace-nowrap">
                                                    {formatStopNumberFromDeliveryTaskOrder(order)}
                                                </div>
                                                <div className="flex-1 min-w-0 border-b border-gray-50 pb-3 group-last:border-0 group-last:pb-0 overflow-hidden">
                                                    <div 
                                                        className="flex items-center justify-between h-12"
                                                        onClick={() => toggleOrderExpansion(order.id)}
                                                    >
                                                        <div 
                                                            className="text-lg text-gray-500 font-bold whitespace-nowrap pr-2 flex-1 min-w-0 overflow-hidden cursor-pointer active:opacity-70 transition-opacity" 
                                                            title={trackingNumber}
                                                            onTouchStart={(e) => {
                                                                e.stopPropagation();
                                                                const timer = setTimeout(() => {
                                                                    handleCopyTrackingNumber(trackingNumber);
                                                                }, 500);
                                                                (e.currentTarget as any)._longPressTimer = timer;
                                                            }}
                                                            onTouchEnd={(e) => {
                                                                const timer = (e.currentTarget as any)._longPressTimer;
                                                                if (timer) {
                                                                    clearTimeout(timer);
                                                                }
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                                const timer = setTimeout(() => {
                                                                    handleCopyTrackingNumber(trackingNumber);
                                                                }, 500);
                                                                (e.currentTarget as any)._longPressTimer = timer;
                                                            }}
                                                            onMouseUp={(e) => {
                                                                const timer = (e.currentTarget as any)._longPressTimer;
                                                                if (timer) {
                                                                    clearTimeout(timer);
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                const timer = (e.currentTarget as any)._longPressTimer;
                                                                if (timer) {
                                                                    clearTimeout(timer);
                                                                }
                                                            }}
                                                        >
                                                            {displayTrackingNumber}
                                                        </div>
                                                        <div className="relative flex-shrink-0 ml-2">
                                                        {(order.orderWithAddress?.order?.deliveryAttempts || 0) > 0 && (
                                                            <div className="absolute -top-4 right-0 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold z-10 shadow-sm border border-white">
                                                                {order.orderWithAddress?.order?.deliveryAttempts}
                                                            </div>
                                                        )}
                                                        <RightOutline 
                                                            className={`text-gray-300 transition-transform duration-200 ${expandedOrderIds[order.id] ? 'rotate-90' : ''}`} 
                                                        />
                                                    </div>
                                                </div>
                                                {expandedOrderIds[order.id] && (
                                                    <div className="mt-4 space-y-3">
                                                        <div className="flex justify-between items-center text-sm">
                                                            <span className="text-gray-500">{t('pages.delivery.orderDetail.route')}:</span>
                                                            <span className="text-gray-900 font-medium">{order.routeName || '-'}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center text-sm">
                                                            <span className="text-gray-500">{t('pages.delivery.orderDetail.assignedTime')}:</span>
                                                            <span className="text-gray-900 font-medium">
                                                                {order.orderWithAddress?.order?.deliverAt ? formatLocalizedDateTime(timestampToDate(order.orderWithAddress.order.deliverAt)) : ''}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between items-center text-sm">
                                                            <span className="text-gray-500">{t('pages.delivery.orderDetail.deliveryAttempt')}:</span>
                                                            <span className="text-gray-900 font-medium">
                                                                {`${order.orderWithAddress?.order?.deliveryAttempts || 0} out of 3`}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                    })}
                                </div>

                                {/* 收件人信息和操作 */}
                                <div className="bg-white px-4 py-3 flex items-center border-t border-gray-50">
                                    <div className="text-lg font-bold text-gray-700 flex-1 truncate min-w-0 pr-2">
                                        {recipient.fullName}
                                    </div>
                                    <div className="flex gap-2 items-center flex-shrink-0">
                                        <Button 
                                            size='small' 
                                            shape='rounded'
                                            className="px-3 py-1.5 flex flex-row items-center justify-center border-none"
                                            onClick={() => { window.location.href = `tel:${recipient.phone}`; }}
                                            style={{ 
                                                paddingLeft: '12px', 
                                                paddingRight: '12px',
                                                '--background-color': '#f0f0f0',
                                                '--text-color': '#4b5563'
                                            } as React.CSSProperties}
                                        >
                                            <div className="flex items-center gap-1">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-blue-500 flex-none">
                                                    <path d="M6.62 10.79c1.44 2.83 3.76 5.21 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                                </svg>
                                                <span className="text-sm whitespace-nowrap leading-none">{t('pages.delivery.orderDetail.call')}</span>
                                            </div>
                                        </Button>
                                        {isSmsEnabled && !isCheckingSms && (
                                            <Button 
                                                size='small' 
                                                shape='rounded'
                                                className="px-3 py-1.5 flex flex-row items-center justify-center border-none"
                                                onClick={() => handleRecipientSms(recipient)}
                                                style={{ 
                                                    paddingLeft: '12px', 
                                                    paddingRight: '12px',
                                                    '--background-color': '#f0f0f0',
                                                    '--text-color': '#4b5563'
                                                } as React.CSSProperties}
                                            >
                                                <div className="flex items-center gap-1">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-blue-500 flex-none">
                                                        <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                                                    </svg>
                                                    <span className="text-sm whitespace-nowrap leading-none">{t('pages.delivery.orderDetail.sms')}</span>
                                                </div>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            {/* 固定在底部的按钮区域 */}
            <div {...getBottomNavBarContainerStyle('flex px-4 py-2 pb-4 gap-4 flex-shrink-0 bg-white border-t border-gray-100')}>
                <Button
                    color='default'
                    onClick={handleFailedActionSheetVisible}
                    block
                    className="h-12 text-lg font-bold"
                    style={{
                        '--background-color': '#6B7280',
                        '--text-color': 'white',
                        border: 'none'
                    }}
                >
                    {t('pages.delivery.orderDetail.failed')}
                </Button>
                <Button
                    color='primary'
                    onClick={handleBatchDeliverySuccess}
                    block
                    className="h-12 text-lg font-bold"
                    style={{
                        '--background-color': '#4F46E5',
                        border: 'none'
                    }}
                >
                    {t('pages.delivery.orderDetail.delivered')}
                </Button>
            </div>

            <ActionSheet
                visible={failedActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className={`text-center`}>{t('pages.delivery.orderDetail.failed')}</h3>
                        <span>{t('pages.delivery.orderDetail.failedDesc')}</span>
                    </div>
                }
                cancelText={t('pages.delivery.detail.cancel')}
                onClose={() => setFailedActionSheetVisible(false)}
                actions={exceptionDictionaries.map((action: ExceptionDictionaryType) => ({
                    text: (
                        <div>
                            <div className="text-purple-700 font-medium text-lg">
                                {getExceptionTypeText(action)}
                            </div>
                        </div>
                    ),
                        key: action.exceptionReasonCode,
                        onClick: () => {
                            setFailedActionSheetVisible(false);
                            const subReasons = action.exceptionSubReasons;
                            if (subReasons && subReasons.length > 0) {
                                setSelectedExceptionForSubReasons(action);
                                setSelectedSubReasonCodes(new Set());
                            } else {
                                handleActionClick(action);
                            }
                        }
                    }))}
                />

                <Popup
                    visible={selectedExceptionForSubReasons !== null}
                    position="bottom"
                    onMaskClick={() => {
                        setSelectedExceptionForSubReasons(null);
                        setSelectedSubReasonCodes(new Set());
                    }}
                    bodyStyle={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
                >
                    <div className="p-4 pb-safe">
                        <h3 className="text-center text-gray-800 font-semibold text-base py-3">
                            {selectedExceptionForSubReasons ? t('pages.delivery.orderDetail.wrongAddressReason') : ''}
                        </h3>
                        <div className="max-h-60 overflow-y-auto mb-4 space-y-2 scrollbar-always-visible pr-3.5">
                            {(selectedExceptionForSubReasons?.exceptionSubReasons ?? []).map(sub => {
                                const checked = selectedSubReasonCodes.has(sub.code);
                                return (
                                    <div
                                        key={sub.code}
                                        className={`flex items-center justify-between py-3 px-4 rounded-xl border-2 bg-white active:opacity-90 ${
                                            checked ? 'border-purple-600' : 'border-gray-200'
                                        }`}
                                        onClick={() => {
                                            setSelectedSubReasonCodes(prev => {
                                                const next = new Set(prev);
                                                if (checked) next.delete(sub.code);
                                                else next.add(sub.code);
                                                return next;
                                            });
                                        }}
                                    >
                                        <span className={`text-base ${checked ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                                            {getLocalizedText({ zh: sub.textZh, en: sub.textEn, es: sub.textEs }, sub.textEn)}
                                        </span>
                                        <div
                                            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                checked ? 'bg-purple-600' : 'border-2 border-gray-300 bg-white'
                                            }`}
                                        >
                                            {checked ? (
                                                <CheckOutline className="text-white" style={{ fontSize: 16 }} />
                                            ) : (
                                                <CheckOutline className="text-gray-300" style={{ fontSize: 16 }} />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Button
                            block
                            color="primary"
                            className="w-full"
                            style={{ '--background-color': '#7c3aed', '--border-radius': '8px' } as React.CSSProperties}
                            disabled={selectedSubReasonCodes.size === 0}
                            onClick={() => {
                                if (selectedExceptionForSubReasons) {
                                    const subReasons = (selectedExceptionForSubReasons.exceptionSubReasons ?? []).filter(
                                        sub => selectedSubReasonCodes.has(sub.code)
                                    );
                                    handleSubReasonConfirm(selectedExceptionForSubReasons, subReasons);
                                }
                            }}
                        >
                            {t('pages.delivery.orderDetail.confirm')}
                        </Button>
                    </div>
                </Popup>

                {/* 新增门禁信息确认弹窗 */}
            <Dialog
                visible={addAccessInfoDialogVisible}
                content={
                    <div className="py-2">
                        <div className="text-base font-semibold mb-3 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoTitle')}
                        </div>
                        <div className="text-base mb-4 text-center">
                            {t('pages.delivery.orderDetail.addAccessInfoConfirm')}
                        </div>
                    </div>
                }
                actions={[
                    [
                        {
                            key: 'cancel',
                            text: t('pages.delivery.orderDetail.cancel'),
                            onClick: () => setAddAccessInfoDialogVisible(false),
                            style: { color: '#4b5563' }
                        },
                        {
                            key: 'confirm',
                            text: t('pages.delivery.orderDetail.confirm'),
                            style: { color: '#60a5fa' },
                            onClick: () => {
                                setAddAccessInfoDialogVisible(false);

                                // 如果在地图模式下且有回调，使用回调在抽屉内显示表单
                                if (fromMapMode && onNavigateToAddForm && currentOrderId) {
                                    onNavigateToAddForm({
                                        orderId: currentOrderId,
                                        addressId: currentAddressId,
                                        trackingNumber: currentTrackingNumber,
                                        address: displayAddress,
                                        addressLat: currentAddressLat as number | undefined,
                                        addressLon: currentAddressLon as number | undefined
                                    });
                                } else {
                                    // 否则使用路由跳转（非地图模式）
                                    navigate(
                                        DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ADD_ACCESS_CODE.replace(
                                            ':trackingNumber',
                                            currentTrackingNumber
                                        ),
                                        {
                                            state: {
                                                addressId: currentAddressId,
                                                trackingNumber: currentTrackingNumber,
                                                address: displayAddress,
                                                addressLat: currentAddressLat as number | undefined,
                                                addressLon: currentAddressLon as number | undefined,
                                                orderId: currentOrderId // 传递订单ID
                                            }
                                        }
                                    );
                                }
                            }
                        }
                    ]
                ]}
                onClose={() => setAddAccessInfoDialogVisible(false)}
            />

            {/* 有效性确认弹窗 */}
            <AccessCodeValidationDialog
                visible={validationDialogVisible}
                pendingAccessCodes={pendingAccessCodes}
                onValidationComplete={handleValidationComplete}
                onClose={() => setValidationDialogVisible(false)}
            />
        </div>
    );
});

OrderDetailTemporaryCard.displayName = 'OrderDetailTemporaryCard';

export default OrderDetailTemporaryCard;
